import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useState } from 'react'
import TravelAgency from "../assets/portfolio/travel_agency.png";
import Cafe from "../assets/portfolio/cafe.png";
import Ecommerce from "../assets/portfolio/Capture.JPG";
import ImageCompressor from "../assets/portfolio/image.png";
import BloodBank from "../assets/portfolio/bloodapp.jpg";
import PortfolioWeb from "../assets/portfolio/portfolioweb.png";

const Projects = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  })

  const [selectedProject, setSelectedProject] = useState(null)

  const projects = [
    {
      title: "Project 1",
      description: "A full-stack web application built with Livewire",
      image: TravelAgency,
      technologies: ["Livewire", "Khalti Payment Gateway", "MySQL", "Tailwind CSS"],
      githubLink: "#",
      details: "Project 1 is a comprehensive full-stack solution designed to showcase modern web development practices. Built with Livewire, it features user authentication, data persistence with MySQL, and responsive design with Tailwind CSS."
    },
    {
      title: "Project 2",
      description: "An e-commerce platform with Khalti payment integration",
      image: Cafe,
      technologies: ["Laravel", "Khalti Payment Gateway", "MySQL", "Bootstrap"],
      githubLink: "#",
      details: "Project 2 is a feature-rich e-commerce platform built using Laravel for backend development and server-side rendering. It integrates the Khalti Payment Gateway for secure digital payments and utilizes MySQL for reliable data storage. Key features include a product catalog, shopping cart, user authentication, order tracking, and an admin dashboard for managing inventory and orders."
    },
    
    {
      title: "Project 3",
      description: "A simple and secure eCommerce website for online shopping and payments.",
      image: Ecommerce,
      technologies: ["WordPress", "Khalti Payment Gateway", "MySQL"],
      githubLink: "#",
      details: "A user-friendly eCommerce website built with WordPress, integrated with the Khalti Payment Gateway and powered by a MySQL database for secure and efficient transaction management."
    },
    {
      title: "Project 4",
      description: "Image compression application",
      image: ImageCompressor,
      technologies: ["Android SDK", "Java", "XML", "SQLite"],

      githubLink: "#",
      details: "Project 4 is an Android application designed for image compression with real-time preview functionality. Users can select images, compress them, and instantly view the results within the app. Compressed images and related data are stored locally using SQLite for offline access and management."
    },
    {
      title: "Project 5",
      description: "Blood donation management platform",
      image: BloodBank,
      technologies: ["Android SDK", "Java", "XML", "SQLite"],
      githubLink: "#",
      details: "Project 5 is a blood donation management platform built using Android SDK, Java, XML, and SQLite. The app allows users to become either donors or recipients and view details of both. Admins can manage all records, ensuring seamless coordination between donors and recipients within the platform."
    },
    
    {
      title: "Project 6",
      description: "Portfolio website",
      image: PortfolioWeb,
      technologies: ["React", "Tailwind CSS", "Framer Motion"],
      githubLink: "#",
      details: "Project 6 is a modern portfolio website built with React and Tailwind CSS. It features smooth animations with Framer Motion for a dynamic user experience. The site is responsive and includes a day/night mode for easy viewing in different lighting. It’s designed to showcase professional work with a clean and simple layout."
    }
    
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  }

  const projectVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  }

  const closeModal = () => {
    setSelectedProject(null)
  }

  return (
    <section id="projects" className="min-h-screen py-16 bg-gray-100 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-extrabold text-gray-900 dark:text-white mb-2">
            <span className="bg-clip-text font-montserrat text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              My Projects
            </span>
          </h2>
          <p className="text-xl font-mono text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Check out some of my work right here.
          </p>
        </motion.div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6"
        >
          {projects.map((project, index) => (
            <motion.div
              key={index}
              variants={projectVariants}
              className="group bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex flex-col border border-gray-100 dark:border-gray-700"
            >
              <div className="relative h-48 overflow-hidden bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
                <img
                  src={project.image}
                  alt={project.title}
                  className="max-w-full max-h-full object-contain"
                  style={{ width: 'auto', height: 'auto' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              <div className="p-4 flex flex-col flex-grow">
                <h3 className="text-lg font-robotoMono font-semibold text-gray-900 dark:text-white mb-1">
                  {project.title}
                </h3>
                <p className="text-sm text-gray-600 font-mono dark:text-gray-200 mb-3">
                  {project.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-3">
                  {project.technologies.map((tech, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 font-mono bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 text-xs rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Push buttons to bottom with flex-grow margin */}
                <div className="mt-auto">
                  <div className="flex space-x-2">
                    <motion.button
                      onClick={() => setSelectedProject(project)}
                      className="flex-1 text-center font-medium hover:font-bold font-robotoMono text-white bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 text-sm py-1.5 rounded-md shadow hover:shadow-md transition-all"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      Details
                    </motion.button>
                    <motion.a
                      href={project.githubLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center font-medium hover:font-bold font-robotoMono text-indigo-600 dark:text-indigo-400 border border-indigo-500 dark:border-indigo-400 text-sm py-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      Code
                    </motion.a>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Project Details Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold font-robotoMono text-gray-900 dark:text-white">
                  {selectedProject.title}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>

              <div className="h-64 bg-gray-50 dark:bg-gray-700 rounded-md mb-4 flex items-center justify-center overflow-hidden">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="max-w-full max-h-full object-contain"
                  style={{ width: 'auto', height: 'auto' }}
                />
              </div>

              <p className="text-gray-700 font-mono text-justify dark:text-gray-200 mb-4">
                {selectedProject.details}
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                {selectedProject.technologies.map((tech, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 font-mono bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 text-xs rounded-full"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              <div className="flex justify-end">
                <motion.a
                  href={selectedProject.githubLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-center font-medium hover:font-bold font-robotoMono text-white bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 text-sm py-2 px-4 rounded-md shadow hover:shadow-md transition-all"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  View Code
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  )
}

export default Projects